import { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, Loader2, Phone, MessageSquare, Clock, AlertTriangle, Check, Bookmark, Plus, Trash2, X, Edit3, Link2, RotateCw, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SmsMessenger() {
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [configured, setConfigured] = useState(null);
  const [fromNumber, setFromNumber] = useState('');
  const [templates, setTemplates] = useState([]);
  const [editingTemplates, setEditingTemplates] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [replyLinks, setReplyLinks] = useState({ fb_username: '', ig_username: '', wa_number: '' });
  const [phonebook, setPhonebook] = useState([]);
  const [showPhonebook, setShowPhonebook] = useState(false);
  const [phonebookQuery, setPhonebookQuery] = useState('');
  const [phonebookLoading, setPhonebookLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/admin/vonage-config`, { withCredentials: true }),
      axios.get(`${API}/api/admin/sms/history`, { withCredentials: true }),
      axios.get(`${API}/api/admin/sms/templates`, { withCredentials: true }),
      axios.get(`${API}/api/admin/sms/reply-links`, { withCredentials: true }),
    ]).then(([cfgRes, histRes, tplRes, linksRes]) => {
      setConfigured(cfgRes.data.configured);
      setFromNumber(cfgRes.data.originator || '');
      setHistory(histRes.data);
      setTemplates(tplRes.data.templates || []);
      setReplyLinks(linksRes.data || { fb_username: '', ig_username: '', wa_number: '' });
    }).catch(() => setConfigured(false));
  }, []);

  const loadPhonebook = async () => {
    if (phonebook.length > 0) { setShowPhonebook(true); return; }
    setPhonebookLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/square/customers`, { withCredentials: true });
      setPhonebook(data.customers || []);
      setShowPhonebook(true);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'No Square account configured — add one in the Square tab.');
    }
    setPhonebookLoading(false);
  };

  const pickContact = (c) => {
    setTo(c.phone.replace(/[^\d]/g, ''));
    setShowPhonebook(false);
    setPhonebookQuery('');
  };

  const filteredPhonebook = phonebook.filter(c =>
    !phonebookQuery || c.name.toLowerCase().includes(phonebookQuery.toLowerCase()) || c.phone.includes(phonebookQuery)
  );

  const insertLink = (snippet) => {
    setMessage(prev => {
      const sep = !prev || prev.endsWith('\n') ? '' : '\n\n';
      return (prev + sep + snippet).slice(0, 1000);
    });
  };

  const deleteOne = async (id) => {
    if (!window.confirm('Delete this message from history?')) return;
    try {
      await axios.delete(`${API}/api/admin/sms/history/${id}`, { withCredentials: true });
      setHistory(prev => prev.filter(h => h.id !== id));
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const deleteAll = async () => {
    if (!window.confirm(`Delete ALL ${history.length} messages from history? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/sms/history`, { withCredentials: true });
      setHistory([]);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to clear history');
    }
  };

  const [resendingId, setResendingId] = useState(null);
  const resendOne = async (h) => {
    if (!window.confirm(`Resend this message to ${h.to}?`)) return;
    setResendingId(h.id);
    try {
      const { data } = await axios.post(`${API}/api/admin/sms/send`, { to: h.to, message: h.message }, { withCredentials: true });
      setHistory(prev => [{ id: data.id, to: h.to, message: h.message, status: 'sent', created_at: new Date().toISOString() }, ...prev]);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Resend failed');
    }
    setResendingId(null);
  };

  const saveTemplates = async (list) => {
    await axios.put(`${API}/api/admin/sms/templates`, { templates: list }, { withCredentials: true });
  };

  const addTemplate = () => {
    if (!newBody.trim()) return;
    const next = [...templates, { title: newTitle.trim() || 'Untitled', body: newBody.trim() }];
    setTemplates(next);
    saveTemplates(next);
    setNewTitle('');
    setNewBody('');
  };

  const removeTemplate = (i) => {
    const next = templates.filter((_, j) => j !== i);
    setTemplates(next);
    saveTemplates(next);
  };

  const sendSms = async (e) => {
    e.preventDefault();
    if (!to.trim() || !message.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/sms/send`, { to: to.trim(), message: message.trim() }, { withCredentials: true });
      setResult({ ok: true });
      setHistory(prev => [{ id: data.id, to: to.trim(), message: message.trim(), status: 'sent', created_at: new Date().toISOString() }, ...prev]);
      setMessage('');
    } catch (err) {
      setResult({ ok: false, error: err.response?.data?.detail || 'Failed to send' });
    }
    setSending(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <h2 className="font-heading text-2xl tracking-wider uppercase text-foreground mb-1">Message the Customer</h2>
        <p className="font-sans text-sm text-muted-foreground">Send text messages to customers in Japan or USA via Vonage SMS.</p>
      </div>

      {/* Not configured warning */}
      {configured === false && (
        <div className="border-2 border-yellow-600/40 bg-yellow-500/5 rounded-xl p-5 flex items-start gap-3" data-testid="vonage-not-configured">
          <AlertTriangle size={20} className="text-yellow-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-heading text-sm tracking-wider uppercase text-yellow-500">Vonage Not Configured</p>
            <p className="font-sans text-xs text-muted-foreground mt-1">Go to the <strong>Settings</strong> tab to add your Vonage API key, API secret, and Sender ID before sending messages.</p>
          </div>
        </div>
      )}

      {/* Saved Templates */}
      <div className="border border-border rounded-xl bg-card p-5" data-testid="sms-templates">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bookmark size={16} className="text-brand" />
            <h3 className="font-heading text-sm tracking-widest uppercase text-foreground">Saved Messages</h3>
            <span className="font-sans text-xs text-muted-foreground">{templates.length}</span>
          </div>
          <button
            onClick={() => setEditingTemplates(!editingTemplates)}
            data-testid="templates-edit-toggle"
            className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground hover:text-brand transition-colors flex items-center gap-1"
          >
            {editingTemplates ? <><X size={12} /> Done</> : <><Edit3 size={12} /> Edit</>}
          </button>
        </div>

        {templates.length === 0 && !editingTemplates && (
          <p className="font-sans text-xs text-muted-foreground italic text-center py-3">No saved messages yet. Click Edit to add templates.</p>
        )}

        {templates.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-2 mb-3">
            {templates.map((t, i) => (
              <div key={i} className="relative group">
                <button
                  onClick={() => setMessage(t.body)}
                  data-testid={`template-use-${i}`}
                  className="w-full text-left border border-border rounded-lg p-3 hover:border-brand/50 hover:bg-brand/5 transition-all"
                >
                  <p className="font-heading text-[10px] tracking-widest uppercase text-brand mb-1">{t.title}</p>
                  <p className="font-sans text-xs text-muted-foreground line-clamp-2">{t.body}</p>
                </button>
                {editingTemplates && (
                  <button
                    onClick={() => removeTemplate(i)}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/40 transition-colors"
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {editingTemplates && (
          <div className="border-t border-border pt-3 mt-3 space-y-2">
            <input
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              placeholder="Template name (e.g. Late Arrival)"
              data-testid="template-title-input"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm text-foreground focus:outline-none focus:border-brand placeholder-muted-foreground"
            />
            <textarea
              value={newBody}
              onChange={e => setNewBody(e.target.value)}
              rows={2}
              placeholder="Message text..."
              data-testid="template-body-input"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 font-sans text-sm text-foreground focus:outline-none focus:border-brand placeholder-muted-foreground resize-none"
            />
            <button onClick={addTemplate} disabled={!newBody.trim()} data-testid="template-add-btn" className="flex items-center gap-1.5 font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-2 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-30">
              <Plus size={12} /> Save Template
            </button>
          </div>
        )}
      </div>

      {/* Send form */}
      <form onSubmit={sendSms} className="border border-border rounded-xl bg-card p-5 space-y-4" data-testid="sms-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block flex items-center gap-2">
              <span>To (Phone Number)</span>
              <button
                type="button"
                onClick={loadPhonebook}
                disabled={phonebookLoading}
                data-testid="sms-phonebook-btn"
                className="ml-auto flex items-center gap-1 text-[10px] tracking-widest uppercase text-muted-foreground hover:text-brand transition-colors normal-case"
                title="Pick from Square customers"
              >
                {phonebookLoading ? <Loader2 size={11} className="animate-spin" /> : <Users size={11} />}
                <span>Square Contacts</span>
              </button>
            </label>
            <div className="relative">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="tel"
                value={to}
                onChange={e => setTo(e.target.value)}
                placeholder="+1 555-123-4567 or +81 90-1234-5678"
                data-testid="sms-to-input"
                className="w-full bg-background border border-border rounded-xl pl-9 pr-4 py-3 font-sans text-sm text-foreground focus:outline-none focus:border-brand placeholder-muted-foreground"
              />
              {showPhonebook && phonebook.length > 0 && (
                <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-2xl overflow-hidden" data-testid="sms-phonebook-dropdown">
                  <div className="flex items-center gap-2 p-2 border-b border-border">
                    <input
                      autoFocus
                      value={phonebookQuery}
                      onChange={e => setPhonebookQuery(e.target.value)}
                      placeholder={`Search ${phonebook.length} customers...`}
                      className="flex-1 bg-background border border-border rounded-lg px-3 py-1.5 font-sans text-xs focus:outline-none focus:border-brand"
                    />
                    <button onClick={() => setShowPhonebook(false)} className="text-muted-foreground hover:text-foreground p-1"><X size={14} /></button>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {filteredPhonebook.length === 0 ? (
                      <p className="font-sans text-xs text-muted-foreground italic text-center py-4">No matches</p>
                    ) : filteredPhonebook.slice(0, 50).map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickContact(c)}
                        data-testid={`sms-phonebook-pick-${c.id}`}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-secondary/40 transition-colors text-left border-b border-border/30 last:border-0"
                      >
                        <div className="w-7 h-7 rounded-full bg-brand/10 flex items-center justify-center font-heading text-[9px] text-brand shrink-0">
                          {c.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-sans text-xs truncate">{c.name}</p>
                          <p className="font-mono text-[10px] text-muted-foreground truncate">{c.phone}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block">From</label>
            <div className="w-full bg-secondary/30 border border-border rounded-xl px-4 py-3 font-sans text-sm text-muted-foreground">
              {fromNumber || 'Not configured'}
            </div>
          </div>
        </div>
        <div>
          <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block">Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={3}
            placeholder="Type your message to the customer..."
            data-testid="sms-message-input"
            className="w-full bg-background border border-border rounded-xl px-4 py-3 font-sans text-sm text-foreground focus:outline-none focus:border-brand placeholder-muted-foreground resize-none"
          />
          <p className="font-sans text-[10px] text-muted-foreground mt-1">{message.length}/160 characters</p>
          {(replyLinks.fb_username || replyLinks.ig_username || replyLinks.wa_number) && (
            <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="reply-link-shortcuts">
              <span className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground flex items-center gap-1"><Link2 size={11} /> Insert Reply Link:</span>
              {replyLinks.fb_username && (
                <button
                  type="button"
                  onClick={() => insertLink(`Reply on Messenger: m.me/${replyLinks.fb_username}`)}
                  data-testid="insert-fb-link"
                  className="font-sans text-[11px] px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/30 hover:bg-blue-500/20 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Facebook Messenger
                </button>
              )}
              {replyLinks.ig_username && (
                <button
                  type="button"
                  onClick={() => insertLink(`Reply on Instagram: ig.me/m/${replyLinks.ig_username}`)}
                  data-testid="insert-ig-link"
                  className="font-sans text-[11px] px-2.5 py-1 rounded-full bg-pink-500/10 text-pink-400 border border-pink-500/30 hover:bg-pink-500/20 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400" /> Instagram
                </button>
              )}
              {replyLinks.wa_number && (
                <button
                  type="button"
                  onClick={() => insertLink(`Reply on WhatsApp: wa.me/${replyLinks.wa_number}`)}
                  data-testid="insert-wa-link"
                  className="font-sans text-[11px] px-2.5 py-1 rounded-full bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 transition-colors flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> WhatsApp
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={sending || !to.trim() || !message.trim() || configured === false}
            data-testid="sms-send-btn"
            className="flex items-center gap-2 font-heading text-sm tracking-widest uppercase bg-brand text-white px-6 py-3 rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-40"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? 'Sending...' : 'Send SMS'}
          </button>
          {result && (
            <span className={`font-sans text-sm flex items-center gap-1 ${result.ok ? 'text-green-400' : 'text-red-400'}`}>
              {result.ok ? <><Check size={14} /> Sent!</> : result.error}
            </span>
          )}
        </div>
      </form>

      {/* History */}
      <div className="border border-border rounded-xl bg-card p-5" data-testid="sms-history">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <MessageSquare size={16} className="text-brand" />
          <h3 className="font-heading text-sm tracking-widest uppercase text-foreground">Message History</h3>
          <span className="font-sans text-xs text-muted-foreground">{history.length} messages</span>
          {history.length > 0 && (
            <button
              onClick={deleteAll}
              data-testid="sms-history-clear-all"
              className="ml-auto flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase text-muted-foreground hover:text-red-400 transition-colors px-2 py-1 rounded border border-border hover:border-red-500/40"
            >
              <Trash2 size={11} /> Clear All
            </button>
          )}
        </div>

        {/* JP delivery-filter help */}
        <div className="rounded-lg bg-yellow-500/5 border border-yellow-700/30 p-3 mb-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-yellow-500 shrink-0 mt-0.5" />
          <div className="text-[11px] font-sans text-muted-foreground leading-relaxed">
            <span className="text-yellow-400 font-semibold">DELIVERED</span> means Vonage handed the SMS to the carrier, but Japan's <strong>NTT DoCoMo / KDDI / SoftBank</strong> default to blocking SMS from alphanumeric senders (なりすまし規制 / 国際SMS拒否). If a customer says "I didn't get it," ask them to turn off <em>international SMS filtering</em> in their phone's Messages settings, or whitelist <span className="font-mono text-foreground">Oki-Escape</span>. <strong>Okinawa Cellular (au)</strong> isn't routed by Vonage at all.
          </div>
        </div>

        {history.length === 0 ? (
          <p className="font-sans text-xs text-muted-foreground italic text-center py-6">No messages sent yet.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {history.map(h => {
              const dlrColor =
                h.dlr_status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                h.dlr_status === 'failed' || h.dlr_status === 'rejected' || h.dlr_status === 'expired' ? 'bg-red-500/20 text-red-400' :
                h.dlr_status ? 'bg-yellow-500/20 text-yellow-400' : '';
              return (
              <div key={h.id} className="group flex gap-3 py-2 border-b border-border/50 last:border-0">
                <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center shrink-0">
                  <Send size={12} className="text-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-foreground">{h.to}</span>
                    <span className={`font-heading text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded ${h.status === 'sent' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{h.status}</span>
                    {h.dlr_status && (
                      <span className={`font-heading text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded ${dlrColor}`} title={`Carrier ${h.dlr_network || '—'} · err ${h.dlr_err_code || '0'}`}>
                        DLR: {h.dlr_status}
                      </span>
                    )}
                    {h.dlr_network && (
                      <span className="font-mono text-[9px] text-muted-foreground">{h.dlr_network}</span>
                    )}
                  </div>
                  <p className="font-sans text-sm text-muted-foreground mt-0.5 truncate">{h.message}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Clock size={10} className="text-muted-foreground" />
                    <span className="font-sans text-[10px] text-muted-foreground">
                      {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(h.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-all">
                  <button
                    onClick={() => resendOne(h)}
                    disabled={resendingId === h.id}
                    data-testid={`sms-history-resend-${h.id}`}
                    title="Resend this message"
                    className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors disabled:opacity-50"
                  >
                    {resendingId === h.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCw size={13} />}
                  </button>
                  <button
                    onClick={() => deleteOne(h.id)}
                    data-testid={`sms-history-delete-${h.id}`}
                    title="Delete this message"
                    className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );})}
          </div>
        )}
      </div>
    </div>
  );
}
